import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { enums, esaar } from '@dha-nexus/shared';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import {
  PageHeader,
  Breadcrumbs,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  StatusChip,
  WorkflowPipeline,
  buildPipelineSteps,
  toast,
} from '../../components/index.js';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import { GlossaryTerm } from '../../components/primitives/GlossaryTerm.jsx';
import { usePeople } from '../../lib/usePeople.js';
import './accessReview.redesign.css';

// Consequence copy shown on the FINAL approve — the admin_provisioning ->
// provisioned edge is the literal moment access is granted for real, so the
// confirm dialog says so plainly instead of the generic "advances to the next
// stop" line.
const PROVISIONING_CONSEQUENCE =
  'Provisioning grants the requested role(s) and division access for real. This is an audited, reversible action.';

// Plain-language fallback for a role / org id when the roster has not resolved.
function prettifyId(id, prefix) {
  if (!id) return '';
  return String(id)
    .replace(prefix, '')
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// Human-readable labels for the canonical 15-state eSAAR workflow.
const STATE_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  supervisor_review: 'Supervisor Review',
  security_review: 'Security / IMO Review',
  portfolio_owner_review: 'Portfolio Owner Review',
  data_steward_review: 'Data Steward Review',
  admin_provisioning: 'Admin Provisioning',
  provisioned: 'Provisioned',
  active: 'Active',
  returned_for_rework: 'Returned for Rework',
  denied: 'Denied',
  withdrawn: 'Withdrawn',
  recertification_due: 'Recertification Due',
  expired: 'Expired',
  revoked: 'Revoked',
};

// Actions grouped + colored by governance intent. Each carries the consequence
// statement its confirm dialog names (AC-STATE-3) and whether the decision
// note is mandatory (terminal / outward-facing decisions) or optional.
const ACTION_GROUPS = [
  {
    label: 'Advance',
    actions: [
      {
        key: 'submit',
        label: 'Submit',
        intent: 'neutral',
        consequence: 'Submitting sends this request into the eSAAR review workflow.',
        requireNote: false,
      },
      {
        key: 'approve',
        label: 'Approve',
        intent: 'approve',
        consequence: 'Approving advances this request to the next review stop in the workflow.',
        requireNote: false,
      },
      {
        key: 'recertify',
        label: 'Recertify',
        intent: 'approve',
        consequence: 'Recertifying routes this active access grant into recertification review.',
        requireNote: false,
      },
    ],
  },
  {
    label: 'Hold / return',
    actions: [
      {
        key: 'requestRework',
        label: 'Request rework',
        intent: 'caution',
        consequence:
          'Requesting rework returns this request to the requester; your note tells them what to fix.',
        requireNote: true,
      },
    ],
  },
  {
    label: 'Terminate',
    actions: [
      {
        key: 'deny',
        label: 'Deny',
        intent: 'deny',
        consequence:
          'Denying is a terminal decision — the requester must file a new eSAAR request to regain access.',
        requireNote: true,
      },
      {
        key: 'revoke',
        label: 'Revoke',
        intent: 'deny',
        consequence:
          'Revoking immediately removes provisioned access. This is a terminal, audited decision.',
        requireNote: true,
      },
    ],
  },
];

export function AccessReviewDetailPage() {
  const { id } = useParams();
  const r = useApi(`/access-requests/${id}`);
  const mut = useMutation();
  const { nameOf } = usePeople();
  // Rosters used to turn provisioning ids into plain-language names for the
  // success toast. Guarded with Array.isArray so a mocked/empty response never
  // throws.
  const rolesApi = useApi('/rbac/roles');
  const orgsApi = useApi('/organizations', { query: { limit: 200 } });
  const roleNameById = new Map(
    (Array.isArray(rolesApi.data?.data) ? rolesApi.data.data : []).map((role) => [
      role.id,
      role.name || role.id,
    ]),
  );
  const orgNameById = new Map(
    (Array.isArray(orgsApi.data?.data) ? orgsApi.data.data : []).map((org) => [
      org.id,
      org.name || org.id,
    ]),
  );
  const roleName = (rid) => roleNameById.get(rid) || prettifyId(rid, /^ROLE-/);
  const orgName = (oid) => orgNameById.get(oid) || prettifyId(oid, /^ORG-(DHA-)?/);
  // SM-06: actions confirm with a decision note, refresh in place (no
  // location.reload()), and only state-valid actions are rendered.
  const [pendingAction, setPendingAction] = useState(null);

  async function act(action, note) {
    try {
      const res = await mut.run(() =>
        api.post(`/access-requests/${id}/actions`, { action: action.key, note: note || null }),
      );
      const provisioning = res?.data?.provisioning;
      if (provisioning && Array.isArray(provisioning.granted) && provisioning.granted.length) {
        // F1 payoff — the grant actually happened; name who now holds what.
        const who = nameOf(data?.requesterUserId) || 'The requester';
        const roles = provisioning.granted.map(roleName).filter(Boolean).join(', ');
        const division = provisioning.homeOrganizationId
          ? orgName(provisioning.homeOrganizationId)
          : null;
        toast.success({
          title: 'Access provisioned',
          body: division
            ? `${who} now holds ${roles} for ${division}.`
            : `${who} now holds ${roles}.`,
        });
      } else {
        toast.success({
          title: `${action.label} recorded`,
          body: `Request ${id} updated — the workflow pipeline reflects the new state.`,
        });
      }
      await r.refresh();
    } catch (error) {
      toast.error({ title: `${action.label} failed`, body: error.message });
    }
  }

  if (r.loading)
    return (
      <>
        <PageHeader title={`Request ${id}`} />
        <LoadingSkeleton rows={6} />
      </>
    );
  if (r.error) return <ErrorState error={r.error} />;
  const data = r.data?.data;
  if (!data) return <EmptyState title="Request not found" />;

  const workflowSteps = data.workflowSteps || [];

  // Force terminal "blocked" states from history so denied/returned are never
  // collapsed into the neutral pending lane of the pipeline.
  const statusByKey = {};
  for (const step of workflowSteps) {
    if (step.status === 'denied') statusByKey.denied = 'blocked';
    if (step.status === 'returned') statusByKey.returned_for_rework = 'blocked';
  }
  // Also reflect the current state if it is itself a blocked/terminal stop.
  const current = data.currentWorkflowState;
  if (current === 'denied') statusByKey.denied = 'blocked';
  if (current === 'returned_for_rework') statusByKey.returned_for_rework = 'blocked';
  if (current === 'expired') statusByKey.expired = 'blocked';
  if (current === 'revoked') statusByKey.revoked = 'blocked';
  if (current === 'withdrawn') statusByKey.withdrawn = 'blocked';

  const pipelineSteps = buildPipelineSteps({
    order: enums.WORKFLOW_STATES,
    current,
    statusByKey,
    labels: STATE_LABELS,
  });

  // SM-06: only render actions the eSAAR state machine allows from the
  // request's current state (shared FSM — the same table the API enforces).
  const allowedActionKeys = new Set(esaar.allowedActions(current));
  const visibleGroups = ACTION_GROUPS.map((group) => ({
    ...group,
    actions: group.actions.filter((action) => allowedActionKeys.has(action.key)),
  })).filter((group) => group.actions.length > 0);

  const historyTone = (status) =>
    status === 'complete'
      ? 'sage'
      : status === 'denied'
        ? 'brick'
        : status === 'returned'
          ? 'amber'
          : 'slate';

  return (
    <>
      <Breadcrumbs
        items={[{ to: '/admin/access-requests', label: 'Access Reviews' }, { label: data.id }]}
      />
      <PageHeader
        eyebrow={
          <>
            <GlossaryTerm term="eSAAR" /> · <span className="muted">{data.id}</span>
          </>
        }
        title={
          <>
            <PersonLabel userId={data.requesterUserId} fallback="This requester" />
            &rsquo;s access request
          </>
        }
        subtitle={
          <>
            Requester <PersonLabel userId={data.requesterUserId} />
            {data.justification ? ` · ${data.justification.slice(0, 140)}` : ''}
          </>
        }
      >
        <StatusChip status={data.currentWorkflowState} />
        <StatusChip status={data.riskLevel} />
      </PageHeader>

      <section className="card access-detail__command" style={{ marginBottom: '1rem' }}>
        <h2 className="card-title">eSAAR workflow pipeline</h2>
        <p className="access-queue__rollup-sub">
          Full 15-state lifecycle · denied and returned stops shown as blocked.
        </p>
        <div className="access-detail__pipeline">
          <WorkflowPipeline steps={pipelineSteps} ariaLabel="eSAAR workflow progress" />
        </div>
      </section>

      <section className="bento">
        <div className="span-8 card">
          <h2 className="card-title">Workflow history</h2>
          {workflowSteps.length === 0 ? (
            <EmptyState title="No workflow steps yet" />
          ) : (
            <ul className="access-detail__history">
              {workflowSteps.map((s, i) => {
                const tone = historyTone(s.status);
                return (
                  <li key={i}>
                    <span className={`access-detail__history-rail tone-${tone}`} aria-hidden />
                    <span>
                      <span className="access-detail__history-name">{s.stepName}</span>
                      <span className="access-detail__history-meta">
                        {s.status}
                        {s.actorUserId ? (
                          <>
                            {' · '}
                            <PersonLabel userId={s.actorUserId} />
                          </>
                        ) : null}
                        {s.note ? ` · ${s.note}` : ''}
                      </span>
                    </span>
                    <span className="access-detail__history-when">
                      {s.completedAt ? new Date(s.completedAt).toLocaleString() : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="span-4 card">
          <h2 className="card-title">Actions</h2>
          {visibleGroups.length === 0 ? (
            <p className="muted small">
              No actions are available from the {STATE_LABELS[current] || current} state.
            </p>
          ) : (
            <div className="access-detail__actions">
              {visibleGroups.map((group) => (
                <div key={group.label}>
                  <div className="access-detail__action-group-label">{group.label}</div>
                  {group.actions.map((a) => (
                    <button
                      key={a.key}
                      className={`btn ghost intent-${a.intent}`}
                      onClick={() => setPendingAction(a)}
                      disabled={mut.running}
                      style={{ width: '100%', marginTop: '0.4rem' }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <ConfirmActionDialog
        open={Boolean(pendingAction)}
        title={`${pendingAction?.label || ''} request ${id}?`}
        body={
          pendingAction?.key === 'approve' && current === 'admin_provisioning'
            ? PROVISIONING_CONSEQUENCE
            : pendingAction?.consequence
        }
        confirmLabel={pendingAction?.label}
        tone={pendingAction?.intent === 'deny' ? 'danger' : 'primary'}
        requireNote={Boolean(pendingAction?.requireNote)}
        showNote
        noteLabel="Decision note"
        notePlaceholder="Capture the basis for this eSAAR decision."
        busy={mut.running}
        onCancel={() => setPendingAction(null)}
        onConfirm={async (note) => {
          const action = pendingAction;
          setPendingAction(null);
          if (action) await act(action, note);
        }}
      />
    </>
  );
}
